import { AccountLinkModel, AccountLinkUserRoleWithPermissions } from '../api/models/users-account-links-model'
import { UserData } from 'src/State/User/UserModel'
import { DEFAULT_HAGER_BRIDGE_PERMISSIONS } from './default-hager-bridge-permissions'
import {
  ChargingCardFeature,
  CountryInfoFeature,
  HagerBridgePermissions,
  SessionsFeature,
  TariffFeature,
  TourGuideFeature,
  WallboxFeature,
} from '../providers/hager-bridge-permissions'
import { SERVICE_LEVELS_ALL } from 'src/Configuration'

const wallboxPermissions: Record<WallboxFeature, AccountLinkUserRoleWithPermissions[]> = {
  assignTariff: ['owner-ro', 'owner-rw', 'user-rw', 'installer-rw'],
  assignToken: ['owner-ro', 'owner-rw', 'installer-rw'],
}

const chargingCardPermissions: Record<ChargingCardFeature, AccountLinkUserRoleWithPermissions[]> = {
  deleteChargingCard: ['owner-ro', 'owner-rw', 'installer-rw'],
  editChargingCard: ['owner-ro', 'owner-rw', 'installer-rw'],
  editChargingCardStatus: ['owner-ro', 'owner-rw', 'user-rw', 'user-ro', 'installer-rw'],
  linkChargingCards: ['owner-ro', 'owner-rw'],
}

const tariffPermissions: Record<TariffFeature, AccountLinkUserRoleWithPermissions[]> = {
  viewTariffs: ['owner-ro', 'owner-rw', 'installer-ro', 'installer-rw', 'user-rw'],
  addTariff: ['owner-ro', 'owner-rw', 'user-rw', 'installer-rw'],
  editTariff: ['owner-ro', 'owner-rw', 'user-rw', 'installer-rw'],
  deleteTariff: ['owner-ro', 'owner-rw', 'user-rw', 'installer-rw'],
}

const sessionsPermissions: Record<SessionsFeature, AccountLinkUserRoleWithPermissions[]> = {
  exportSessions: ['owner-ro', 'owner-rw', 'user-rw', 'user-ro'],
}

const tourGuidePermissions: Record<TourGuideFeature, AccountLinkUserRoleWithPermissions[]> = {
  viewTourGuide: ['owner-ro', 'owner-rw', 'installer-rw', 'installer-ro'],
}

const countryInfoPermissions: Record<CountryInfoFeature, AccountLinkUserRoleWithPermissions[]> = {
  countryInfo: ['owner-ro', 'owner-rw', 'installer-rw', 'installer-ro'],
}

/**
 * E-Mobility V2 features are built on top of a backend service implemented by P3 called the HagerBridge which allows existing E3/DC
 * backend services to become compliant with OCPP V1.6 protocol. As a part of the backend implementation, various restrictions are
 * imposed for logged in users of different user levels and their role and access rights within a single installation.
 *
 * For this, we must in advance populate a permissions matrix for each user for each feature(e.g "editing a tariff" can only be done
 * by the owner of the installation or service users). This is a pattern commonly seen in the core E3/DC features, BUT HagerBridge
 * is not 100% compatible with permissions set in core services, so we must explicitly provide a new permission mapping of allowed
 * features for each logged-in user.
 *
 * * @returns accessGranted serves as a confirmation that the user has a valid role and role inside an installation. Ther are, however,
 * cases where the user should not be granted access: for example, logged in user modifies the url and replaces the installation id
 * for which they have access and replace it with a random installation id. In this case, all e-mobility endpoints will throw an
 * authorization error, therefore we show an error message at the top of the e-mobility section by checking the "accessGranted" flag.
 */
export const getHagerBridgePermissions = (accountLinks: AccountLinkModel[], userData: UserData | undefined): HagerBridgePermissions => {
  if (!userData) {
    return DEFAULT_HAGER_BRIDGE_PERMISSIONS
  }

  const accountLink = accountLinks.find(accountLink => {
    if (accountLink.accountType === 'company') {
      return accountLink.accountId === userData.Firmen_ID
    }

    return accountLink.userName === userData.username
  })

  const userRole: AccountLinkUserRoleWithPermissions | undefined = accountLink ? `${accountLink.role}-${accountLink.permissionType}` : undefined
  const hasAbsoluteRights = SERVICE_LEVELS_ALL.includes(userData.level)

  return {
    accessGranted: hasAbsoluteRights || Boolean(userRole),
    wallboxPermissions: {
      assignTariff: wallboxPermissions.assignTariff.some(allowedRole => allowedRole === userRole || hasAbsoluteRights),
      assignToken: wallboxPermissions.assignToken.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
    },
    chargingCardPermissions: {
      deleteChargingCard: chargingCardPermissions.deleteChargingCard.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
      editChargingCard: chargingCardPermissions.editChargingCard.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
      editChargingCardStatus: chargingCardPermissions.editChargingCardStatus.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
      linkChargingCards: chargingCardPermissions.linkChargingCards.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
    },
    tariffPermissions: {
      viewTariffs: tariffPermissions.viewTariffs.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
      addTariff: tariffPermissions.addTariff.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
      editTariff: tariffPermissions.editTariff.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
      deleteTariff: tariffPermissions.deleteTariff.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
    },
    sessionPermissions: {
      exportSessions: sessionsPermissions.exportSessions.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
    },
    tourGuidePermissions: {
      viewTourGuide: tourGuidePermissions.viewTourGuide.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
    },
    countryInfoPermissions: {
      countryInfo: countryInfoPermissions.countryInfo.some(allowedRole => allowedRole === userRole) || hasAbsoluteRights,
    },
  }
}
